/**
 * @fileoverview
 * This file contains the logic for generating fields that can be used in the client portal logic builder.
 * It handles the mapping of entity properties (from case, company, individual) to query builder fields
 * that can be used in the logic UI and evaluation.
 *
 * The main function `getLogicBuilderFields` returns a list of field groups organized by entity type,
 * with each field properly formatted for use in the query builder component.
 */
import { match } from 'ts-pattern';

import {
  ClientPortalBlockFieldModel,
  ClientPortalBlockModel,
  ClientPortalFieldMappingTypeEnum,
  ClientPortalStepModel,
  ClientPortalStepTypeEnum,
  ClientPortalTypeEnum,
  PropertyMappingEntityEnum,
} from '../../../shared/models';
import {
  generateKeyFromMapping,
  Property,
  PropertyTypeEnum,
  QueryBuilderField,
  QueryBuilderFieldGroup,
} from '../../../shared/properties';
import { matchFieldMapping } from '../client-portal-block';
import {
  ClientPortalProperty,
  getClientPortalCustomPropertyAdapter,
} from '../client-portal-properties';
import { clientPortalCompanyPropertiesDefinition } from '../client-portal-properties/client-portal-company-properties.definition';
import { clientPortalIndividualPropertiesDefinition } from '../client-portal-properties/client-portal-individual-properties.definition';
import {
  UNSUPPORTED_PROPERTY_MAPPING_KEYS,
  UNSUPPORTED_PROPERTY_TYPES,
} from './constants';

/**
 * Organizes query builder fields by entity type
 */
type LogicBuilderFieldByEntity = {
  case: QueryBuilderField[];
  mainCompany: QueryBuilderField[];
  individual: QueryBuilderField[];
  affiliatedCompany: QueryBuilderField[];
};

/**
 * Parameters for the getLogicBuilderFields function
 */
type GetLogicBuilderFieldsParams = {
  /**
   * The type of client portal (KYB or KYC) which determines available entities
   */
  clientPortalType: ClientPortalTypeEnum;

  /**
   * Custom properties defined in the workspace that should be available in the logic builder
   */
  customProperties: Parameters<
    typeof getClientPortalCustomPropertyAdapter
  >[0][];

  /**
   * Optional step information to determine context-specific fields
   *
   * - Omit to get the fields for any steps (logic at the **step** level)
   * - Set to get the fields available for the blocks of a specific step (logic at the **block** level)
   */
  step?: Pick<ClientPortalStepModel, 'type'> & {
    blocks: (
      | Partial<Omit<ClientPortalBlockModel, 'logics'>>
      | Pick<ClientPortalBlockFieldModel, 'mapping'>
    )[];
  };
};

export type ClientPortalLogicContextType =
  | 'case' // Case entity context
  | 'main_company' // Main company context (for KYB)
  | 'individual' // Individual context (for KYC or individuals in KYB)
  | 'affiliated_company'; // Affiliated company context (for KYB)

/**
 * Generates field groups for the client portal logic builder
 *
 * This function takes client portal configuration and returns a list of field groups
 * that can be used in the logic builder UI. The fields are organized by entity type
 * and include both default and custom properties.
 *
 * @param params Configuration parameters including client portal type, step info, and custom properties
 * @returns Array of field groups organized by entity type (case, company, individual)
 */
export const getLogicBuilderFields = ({
  clientPortalType,
  step,
  customProperties: customPropertiesAsCustomProperties,
}: GetLogicBuilderFieldsParams): QueryBuilderFieldGroup[] => {
  // Convert custom properties to the internal property format
  const customPropertiesAsProperties = customPropertiesAsCustomProperties.map(
    getClientPortalCustomPropertyAdapter,
  );

  // Get fields organized by entity type
  const fields = getLogicBuilderFieldsByEntity(
    clientPortalType,
    customPropertiesAsProperties,
    step,
  );

  // Create field groups from the organized fields
  const fieldGroups: QueryBuilderFieldGroup[] = [];

  // Only add groups that have fields
  if (fields.case.length > 0) {
    fieldGroups.push({ label: 'Case', options: fields.case });
  }

  if (fields.mainCompany.length > 0) {
    fieldGroups.push({ label: 'Main company', options: fields.mainCompany });
  }

  if (fields.individual.length > 0) {
    fieldGroups.push({ label: 'Individual', options: fields.individual });
  }

  if (fields.affiliatedCompany.length > 0) {
    fieldGroups.push({
      label: 'Affiliated company',
      options: fields.affiliatedCompany,
    });
  }

  return fieldGroups;
};

/**
 * Organizes logic builder fields by entity type based on client portal configuration
 *
 * This function determines which fields should be available for each entity type
 * based on the client portal type and step configuration.
 *
 * @param clientPortalType The type of client portal (KYB or KYC)
 * @param customProperties Custom properties defined in the workspace
 * @param step Optional step information to determine context-specific fields
 * @returns Fields organized by entity type
 */
const getLogicBuilderFieldsByEntity = (
  clientPortalType: ClientPortalTypeEnum,
  customProperties: Property<PropertyTypeEnum>[],
  step: GetLogicBuilderFieldsParams['step'],
): LogicBuilderFieldByEntity => {
  // Extract field mappings from step blocks
  const stepFields = step?.blocks.filter((block) => 'mapping' in block) ?? [];

  // Initialize empty arrays for each entity type
  const fieldsByEntities: LogicBuilderFieldByEntity = {
    case: [],
    mainCompany: [],
    individual: [],
    affiliatedCompany: [],
  };

  // Case properties are always available for both client portal types
  addSupportedEntityPropertiesToFields(fieldsByEntities.case, {
    customProperties,
    defaultProperties: {}, // @NOTE There is no default case properties yet
    contextType: 'case',
    entityMappingType: PropertyMappingEntityEnum.case,
    stepFields,
  });

  // Add entity-specific fields based on client portal type
  if (clientPortalType === ClientPortalTypeEnum.KYB) {
    // Main company properties are always available for KYB client portal
    addSupportedEntityPropertiesToFields(fieldsByEntities.mainCompany, {
      customProperties,
      defaultProperties: clientPortalCompanyPropertiesDefinition,
      contextType: 'main_company',
      entityMappingType: PropertyMappingEntityEnum.company,
      stepFields:
        // When the step is affiliated_companies_edit then all the fields of the steps are for the affiliated_company
        // context and all the main_company fields are never local
        step?.type === ClientPortalStepTypeEnum.affiliated_companies_edit
          ? []
          : stepFields,
    });
  }

  if (clientPortalType === ClientPortalTypeEnum.KYC) {
    // Individual properties are always available for KYC client portal
    addSupportedEntityPropertiesToFields(fieldsByEntities.individual, {
      customProperties,
      defaultProperties: clientPortalIndividualPropertiesDefinition,
      contextType: 'individual',
      entityMappingType: PropertyMappingEntityEnum.individual,
      stepFields,
    });
  }

  // Add step-specific entity fields
  if (step) {
    match(step.type)
      .with(ClientPortalStepTypeEnum.affiliated_companies_edit, () => {
        // Affiliated company properties are available for KYB client portal
        // on the affiliated_companies_edit step
        addSupportedEntityPropertiesToFields(
          fieldsByEntities.affiliatedCompany,
          {
            customProperties,
            defaultProperties: clientPortalCompanyPropertiesDefinition,
            contextType: 'affiliated_company',
            entityMappingType: PropertyMappingEntityEnum.company,
            stepFields,
          },
        );
      })
      .with(ClientPortalStepTypeEnum.individuals_edit, () => {
        // Individual properties are available for KYB client portal
        // on the individuals_edit step
        addSupportedEntityPropertiesToFields(fieldsByEntities.individual, {
          customProperties,
          defaultProperties: clientPortalIndividualPropertiesDefinition,
          contextType: 'individual',
          entityMappingType: PropertyMappingEntityEnum.individual,
          stepFields,
        });
      });
  }

  return fieldsByEntities;
};

/**
 * Adds supported properties from an entity to the fields array
 *
 * This function filters and transforms properties from an entity (both default and custom)
 * into query builder fields and adds them to the provided fields array.
 *
 * @param fields The array to add the fields to
 * @param params Configuration parameters including properties, context type, and mapping info
 */
const addSupportedEntityPropertiesToFields = (
  fields: QueryBuilderField[],
  {
    customProperties,
    defaultProperties,
    contextType,
    entityMappingType,
    stepFields,
  }: {
    customProperties: Property<PropertyTypeEnum>[];
    defaultProperties?: Record<string, ClientPortalProperty>;
    contextType: ClientPortalLogicContextType;
    entityMappingType: PropertyMappingEntityEnum;
    stepFields: Pick<ClientPortalBlockFieldModel, 'mapping'>[];
  },
) => {
  // Add default properties if provided
  if (defaultProperties) {
    fields.push(
      ...Object.values(defaultProperties)
        .filter(isPropertySupported)
        .map((property) => {
          // Check if this property is used in the current step (local)
          const matchPropertyMapping = matchFieldMapping(property);
          const isLocal = stepFields.some((stepField) =>
            matchPropertyMapping(stepField),
          );

          return propertyToLogicBuilderField(contextType, property, isLocal);
        }),
    );
  }

  // Add custom properties for this entity type
  fields.push(
    ...customProperties
      .filter((property) => property.mapping.entity === entityMappingType)
      .map((property) => {
        // Check if this custom property is used in the current step (local)
        const matchPropertyMapping = matchFieldMapping({
          mapping: {
            ...property.mapping,
            type: ClientPortalFieldMappingTypeEnum.custom,
          },
        });
        const isLocal = stepFields.some((stepField) =>
          matchPropertyMapping(stepField),
        );

        return propertyToLogicBuilderField(contextType, property, isLocal);
      }),
  );
};

/**
 * Determines if a property is supported for use in the logic builder
 *
 * Properties may be unsupported if they have complex types that can't be easily
 * represented in the logic UI or if they're explicitly excluded.
 *
 * @param property The property to check
 * @returns True if the property is supported, false otherwise
 */
const isPropertySupported = (property: ClientPortalProperty): boolean =>
  !UNSUPPORTED_PROPERTY_TYPES.includes(property.type) &&
  !UNSUPPORTED_PROPERTY_MAPPING_KEYS.includes(property.mapping.key);

/**
 * Converts a property to a query builder field format
 *
 * This function transforms a property into the format expected by the query builder,
 * including adding context prefixes, handling options, and marking local fields.
 *
 * @param context The context type (case, company, individual)
 * @param property The property to convert
 * @param isLocal Whether the property is used in the current step
 * @returns A query builder field representation of the property
 */
const propertyToLogicBuilderField = (
  context: ClientPortalLogicContextType,
  property: Property<PropertyTypeEnum>,
  isLocal: boolean,
): QueryBuilderField => {
  return {
    // Create a fully qualified name with context prefix
    name: `${context}.${generateKeyFromMapping(property.mapping)}`,
    label: property.label,
    selectedPrefix: context,
    suffix: isLocal ? '(In step)' : undefined,

    // Pass through property type and options
    type: property.type,
    values:
      property.settings &&
      'options' in property.settings &&
      property.settings.options
        ? property.settings.options.map((option) => ({
            label: option.label,
            value: option.key,
          }))
        : undefined,
    isLocal,
    property,
  };
};
